import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonService } from '../../shared/services/common-service/common-service';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import {
  CountryInterface,
  LanguageInterface,
} from '../../shared/interfaces/create-surveys.interface';
import { DefaultSupplierAllocationsService } from './default-supplier-allocations.service';
import { notifyMessage } from '../../constants/notify-message';
import { HttpStatusCode } from '@angular/common/http';

@Component({
  selector: 'ps-default-supplier-allocations',
  templateUrl: './default-supplier-allocations.component.html',
  styleUrls: ['./default-supplier-allocations.component.css'],
})
export class DefaultSupplierAllocationsComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  public countries: CountryInterface[] = [];
  public selectedCountry?: CountryInterface;
  public selectedLanguage?: LanguageInterface;
  public languages: LanguageInterface[] = [];
  public supplierAllocations: number | null = null;
  public supplierFlexibility: number | null = null;
  public currentAllocationId?: number;
  public isLoading: boolean = false;

  constructor(
    private _commonService: CommonService,
    private _defaultSupplierAllocationsService: DefaultSupplierAllocationsService,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this._loadCountries();
  }

  private _loadCountries() {
    const countries = this._commonService.getCountries().subscribe(
      (countriesData) => {
        this.countries = countriesData;
      },
      (err) => {
        this._toastr.error(err.error.msg);
      }
    );
    this._subscriptions.add(countries);
  }

  selectCountry(country: CountryInterface) {
    this.selectedLanguage = undefined;
    this.selectedCountry = country;
    this.languages = country.lang;
    this._resetForm();
  }

  selectLanguage(language: LanguageInterface) {
    if (!this.selectedCountry) {
      this.selectedLanguage = undefined;
      return;
    }
    this.selectedLanguage = language;
    this._loadAllocationData();
  }

  private _resetForm() {
    this.supplierAllocations = null;
    this.supplierFlexibility = null;
    this.currentAllocationId = undefined;
  }

  onFlexibilityInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input) {
      const value = parseFloat(input.value);
      if (!isNaN(value)) {
        if (value > 100) {
          input.value = '100';
          this.supplierFlexibility = 100;
        } else if (value < 0) {
          input.value = '0';
          this.supplierFlexibility = 0;
        } else {
          this.supplierFlexibility = value;
        }
      }
    }
  }

  onFlexibilityChange() {
    if (this.supplierFlexibility !== null) {
      if (this.supplierFlexibility > 100) {
        this.supplierFlexibility = 100;
      } else if (this.supplierFlexibility < 0) {
        this.supplierFlexibility = 0;
      }
    }
  }

  private _getLocale(): string {
    if (!this.selectedLanguage || !this.selectedCountry) {
      return '';
    }
    if (!this.selectedLanguage.transalte_code) {
      return '';
    }
    return `${this.selectedLanguage.transalte_code}_${this.selectedCountry.short_Code}`;
  }

  private _loadAllocationData() {
    if (!this.selectedCountry || !this.selectedLanguage) {
      return;
    }

    const locale = this._getLocale();
    if (!locale) {
      return;
    }

    this.isLoading = true;
    const getAllocationSub = this._defaultSupplierAllocationsService
      .getDefaultSupplierAllocation(locale)
      .subscribe(
        (response) => {
          this.currentAllocationId = response.id;
          this.supplierAllocations = response.allocation;
          this.supplierFlexibility = response.flexibility;
          this.isLoading = false;
        },
        (error) => {
          // If 400 with error code 1014 or 404, it means no allocation exists yet
          if (
            error.status === HttpStatusCode.BadRequest &&
            error.error?.ps_api_response_code === 1014
          ) {
            this._resetForm();
          } else if (error.status === HttpStatusCode.NotFound) {
            this._resetForm();
          } else {
            this._toastr.error(
              error.error?.ps_api_response_message ||
                error.error?.msg ||
                notifyMessage.errorMessage.SERVER_ERROR
            );
          }
          this.isLoading = false;
        }
      );
    this._subscriptions.add(getAllocationSub);
  }

  saveAllocation() {
    if (!this.selectedCountry || !this.selectedLanguage) {
      this._toastr.error('Please select Country and Language');
      return;
    }

    if (
      this.supplierAllocations === null ||
      this.supplierFlexibility === null
    ) {
      this._toastr.error(
        'Please enter Supplier Allocations and Supplier Flexibility'
      );
      return;
    }

    if (this.supplierFlexibility > 100) {
      this._toastr.error('Supplier Flexibility cannot be more than 100');
      return;
    }

    const locale = this._getLocale();
    if (!locale) {
      this._toastr.error('Invalid locale');
      return;
    }

    if (this.currentAllocationId) {
      this._updateAllocation();
    } else {
      this._createAllocation(locale);
    }
  }

  private _createAllocation(locale: string) {
    const payload = {
      buyerId: 0,
      locale: locale,
      allocation: this.supplierAllocations!,
      flexibility: this.supplierFlexibility!,
    };

    const createAllocationSub = this._defaultSupplierAllocationsService
      .createDefaultSupplierAllocation(payload)
      .subscribe(
        (response) => {
          this.currentAllocationId = response.id;
          this._toastr.success(
            'Default Supplier Allocation created successfully'
          );
        },
        (error) => {
          this._toastr.error(
            error.error?.msg || notifyMessage.errorMessage.SERVER_ERROR
          );
        }
      );
    this._subscriptions.add(createAllocationSub);
  }

  private _updateAllocation() {
    if (!this.currentAllocationId) {
      return;
    }

    const payload = {
      allocation: this.supplierAllocations!,
      flexibility: this.supplierFlexibility!,
    };

    const updateAllocatioSub = this._defaultSupplierAllocationsService
      .updateDefaultSupplierAllocation(this.currentAllocationId, payload)
      .subscribe(
        () => {
          this._toastr.success(
            'Default Supplier Allocation updated successfully'
          );
        },
        (error) => {
          this._toastr.error(
            error.error?.msg || notifyMessage.errorMessage.SERVER_ERROR
          );
        }
      );
    this._subscriptions.add(updateAllocatioSub);
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
